import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import trainingLibraryService, { TrainingVideo } from '../services/trainingLibraryService';

const CATEGORY_LABELS: Record<string, string> = {
    general: 'General',
    running: 'Running',
    swimming: 'Swimming',
    cycling: 'Cycling',
    strength: 'Strength',
};

const CATEGORY_STYLES: Record<string, string> = {
    general: 'bg-slate-100 text-slate-700',
    running: 'bg-orange-100 text-orange-700',
    swimming: 'bg-cyan-100 text-cyan-700',
    cycling: 'bg-yellow-100 text-yellow-700',
    strength: 'bg-purple-100 text-purple-700',
};

const extractYouTubeId = (url: string): string | null => {
    const patterns = [
        /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
        /(?:youtu\.be\/)([^?\s]+)/,
        /(?:youtube\.com\/embed\/)([^?\s]+)/,
    ];
    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match?.[1]) return match[1];
    }
    return null;
};

const normalizeCategory = (category: string | null): string => category?.toLowerCase() || 'general';

const formatDate = (value: string): string => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const TrainingVideoPlayer: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const [video, setVideo] = useState<TrainingVideo | null>(null);
    const [allVideos, setAllVideos] = useState<TrainingVideo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [descExpanded, setDescExpanded] = useState(false);

    useEffect(() => {
        let mounted = true;
        const fetch = async () => {
            if (!id) { setError(t('trainingVideoPlayer.errorMissingId')); setLoading(false); return; }
            try {
                setLoading(true);
                setError(null);
                const [data, all] = await Promise.all([
                    trainingLibraryService.getVideoById(id),
                    trainingLibraryService.getPublishedVideos(),
                ]);
                if (mounted) { setVideo(data); setAllVideos(all); }
            } catch {
                if (mounted) setError(t('trainingVideoPlayer.errorLoad'));
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetch();
        return () => { mounted = false; };
    }, [id, t]);

    const youtubeId = video ? extractYouTubeId(video.youtubeUrl) : null;
    const embedUrl = youtubeId
        ? `https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1&playsinline=1`
        : null;
    const category = video ? normalizeCategory(video.category) : 'general';

    const related = allVideos.filter(
        (v) => v.id !== id && normalizeCategory(v.category) === category
    );

    return (
        <div className="min-h-screen bg-[#f9f9f9] flex flex-col">
            {/* Header */}
            <header className="h-16 bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center px-6 shadow-md sticky top-0 z-20 overflow-hidden flex-shrink-0">
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
                <div className="relative z-10 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/training-library')}
                        className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 text-white border border-white/20 flex items-center justify-center transition-colors flex-shrink-0"
                    >
                        <i className="fa-solid fa-arrow-left text-sm"></i>
                    </button>
                    <div>
                        <h1 className="text-base font-bold text-white leading-tight">{t('trainingVideoPlayer.headerTitle')}</h1>
                        <p className="text-cyan-100 text-[11px]">{t('trainingVideoPlayer.headerSubtitle')}</p>
                    </div>
                </div>
            </header>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
                {loading && (
                    <div className="flex items-center justify-center py-32 text-slate-400 gap-3">
                        <i className="fa-solid fa-spinner fa-spin text-2xl"></i>
                        <span>{t('trainingVideoPlayer.loading')}</span>
                    </div>
                )}

                {!loading && error && (
                    <div className="flex items-center justify-center py-32 gap-3 text-red-400">
                        <i className="fa-solid fa-circle-exclamation text-2xl"></i>
                        <span>{error}</span>
                    </div>
                )}

                {!loading && !error && video && (
                    <div className="flex flex-col xl:flex-row gap-0 xl:gap-6 p-4 xl:p-6 xl:items-start">

                        {/* ── Left: Player + info ── */}
                        <div className="flex-1 min-w-0 flex flex-col gap-3">

                            {/* Player */}
                            <div className="w-full aspect-video bg-black rounded-xl overflow-hidden">
                                {embedUrl ? (
                                    <iframe
                                        className="w-full h-full"
                                        src={embedUrl}
                                        title={video.title}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        allowFullScreen
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                                        <i className="fa-solid fa-video-slash text-4xl"></i>
                                    </div>
                                )}
                            </div>

                            {/* Title row */}
                            <div className="bg-white rounded-xl px-5 py-4 shadow-sm border border-slate-100">
                                <h2 className="text-lg font-bold text-slate-900 leading-snug">{video.title}</h2>

                                <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                                    <div className="flex items-center gap-3">
                                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full capitalize ${CATEGORY_STYLES[category] || CATEGORY_STYLES.general}`}>
                                            {CATEGORY_LABELS[category] || t(`trainingLibrary.filters.${category}`)}
                                        </span>
                                        <span className="text-xs text-slate-400">{formatDate(video.createdAt)}</span>
                                    </div>
                                    <a
                                        href={video.youtubeUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-colors"
                                    >
                        <i className="fa-brands fa-youtube text-base"></i>
                                        {t('trainingVideoPlayer.watchOnYoutube')}
                                    </a>
                                </div>
                            </div>

                            {/* Description */}
                            <div className="bg-white rounded-xl px-5 py-4 shadow-sm border border-slate-100">
                                <div
                                    className={`text-sm text-slate-700 leading-relaxed whitespace-pre-wrap ${!descExpanded ? 'line-clamp-3' : ''}`}
                                >
                                    {video.description || t('trainingVideoPlayer.noDescription')}
                                </div>
                                {video.description && video.description.length > 120 && (
                                    <button
                                        onClick={() => setDescExpanded(!descExpanded)}
                                        className="mt-2 text-xs font-bold text-cyan-600 hover:text-cyan-700 transition-colors"
                                    >
                                        {descExpanded ? t('trainingVideoPlayer.showLess') : t('trainingVideoPlayer.showMore')}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ── Right: Related videos ── */}
                        <div className="w-full xl:w-[360px] flex-shrink-0 flex flex-col gap-3">
                            <h3 className="text-sm font-bold text-slate-700 px-1">
                                {related.length > 0 ? t('trainingVideoPlayer.relatedTitle') : t('trainingVideoPlayer.relatedEmpty')}
                            </h3>

                            {related.length === 0 && (
                                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-8 flex flex-col items-center text-slate-400 gap-3">
                                    <i className="fa-solid fa-film text-3xl"></i>
                                    <p className="text-sm text-center">{t('trainingVideoPlayer.relatedEmptyHint')}</p>
                                </div>
                            )}

                            {related.map((v) => {
                                const vid = extractYouTubeId(v.youtubeUrl);
                                const thumb = vid ? `https://img.youtube.com/vi/${vid}/mqdefault.jpg` : null;
                                const cat = normalizeCategory(v.category);
                                return (
                                    <div
                                        key={v.id}
                                        onClick={() => navigate(`/training-library/${v.id}`)}
                                        className="flex gap-3 bg-white rounded-xl border border-slate-100 shadow-sm p-2 cursor-pointer hover:shadow-md transition-shadow group"
                                    >
                                        {/* Thumbnail */}
                                        <div className="relative w-40 aspect-video flex-shrink-0 bg-slate-900 rounded-lg overflow-hidden">
                                            {thumb ? (
                                                <img src={thumb} alt={v.title} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-slate-500">
                                                    <i className="fa-solid fa-video"></i>
                                                </div>
                                            )}
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <div className="w-9 h-9 bg-red-600 rounded-full flex items-center justify-center">
                                                    <i className="fa-solid fa-play text-white text-sm ml-0.5"></i>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Info */}
                                        <div className="flex flex-col justify-center gap-1 min-w-0">
                                            <h4 className="text-xs font-bold text-slate-800 line-clamp-2 leading-snug group-hover:text-cyan-600 transition-colors">
                                                {v.title}
                                            </h4>
                                            <span className={`self-start text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${CATEGORY_STYLES[cat] || CATEGORY_STYLES.general}`}>
                                                {t(`trainingLibrary.filters.${cat}`) || cat}
                                            </span>
                                            <span className="text-[10px] text-slate-400">{formatDate(v.createdAt)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
};

export default TrainingVideoPlayer;
